import { useParams } from 'react-router-dom'
import { useProtocolKey, useProcotol, useKeyTypes } from '@services/protocols/protocols.hook'
import { KeyTypes, PROTOCOL_TYPE } from '@services/protocols/protocols.consts'
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button
} from '@mui/material'
import { DeleteForever } from '@mui/icons-material'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { getProtocols } from '@services/protocols/protocols.helpers'

function ProtocolKeys({ isReadOnly }: { isReadOnly: boolean }) {
  const { id } = useParams<{ id: string }>()
  const { protocol } = useProcotol(Number(id))
  const { currentProtocol, oppositeProtocol } = getProtocols(protocol)
  const { keyTypes } = useKeyTypes()

  const { protocolKey, createProtocolKey, deleteProtocolKey } = useProtocolKey(
    Number(currentProtocol?.id)
  )

  const { protocolKey: oppositeProtocolKey } = useProtocolKey(Number(oppositeProtocol?.id))

  const furnitureCreateform = useFormik({
    validateOnMount: false,
    validateOnChange: false,
    initialValues: {
      keyType: (keyTypes[0]?.name || '') as KeyTypes,
      quantity: 1,
      comment: ''
    },
    validationSchema: Yup.object().shape({
      keyType: Yup.string().required('Pole wymagane'),
      quantity: Yup.number().required('Pole wymagane')
    }),
    onSubmit: async (values: { keyType: KeyTypes; quantity: number; comment: string }) => {
      await createProtocolKey.mutateAsync({
        keyType: values.keyType,
        quantity: values.quantity,
        comment: values.comment || null
      })
      furnitureCreateform.resetForm()
    }
  })

  const getKetTypeName = (type: string) => type

  const removeProtocolFurniture = async (furnitureId: number) => {
    furnitureCreateform.setSubmitting(true)
    await deleteProtocolKey.mutateAsync(furnitureId)
    furnitureCreateform.setSubmitting(false)
  }

  const filteredSelectorTypes = keyTypes
    .filter((item) => item.isActive)
    .filter((item) => !protocolKey.find((keyItem) => keyItem.keyType === item.name))
    .sort((a, b) => a.order - b.order)

  return (
    <Box>
      {protocolKey.length === 0 && oppositeProtocolKey.length === 0 ? (
        <Typography variant='caption' fontWeight={700} display='block' mb={4}>
          Brak kluczy
        </Typography>
      ) : (
        <TableContainer>
          <Table sx={{ minWidth: '100%' }} aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell>Typ</TableCell>
                <TableCell align='right'>Ilość</TableCell>
                <TableCell align='right'>Komentarz</TableCell>
                <TableCell align='right'></TableCell>
              </TableRow>
            </TableHead>

            {currentProtocol?.protocolType === PROTOCOL_TYPE.acceptance ? (
              <TableBody>
                {protocolKey.map((keyItem) => (
                  <TableRow
                    key={keyItem.id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component='th' scope='row'>
                      {getKetTypeName(keyItem.keyType)}
                    </TableCell>
                    <TableCell align='right'> {keyItem.quantity}</TableCell>
                    <TableCell align='right'> {keyItem.comment}</TableCell>
                    <TableCell align='right'>
                      <IconButton
                        disabled={furnitureCreateform.isSubmitting}
                        onClick={() => removeProtocolFurniture(keyItem.id)}
                      >
                        <DeleteForever />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            ) : (
              <TableBody>
                <>
                  {oppositeProtocolKey.map((keyItem) => (
                    <TableRow
                      key={keyItem.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component='th' scope='row'>
                        {getKetTypeName(keyItem.keyType)}
                      </TableCell>
                      <TableCell align='right'> {keyItem.quantity}</TableCell>
                      <TableCell align='right'> {keyItem.comment}</TableCell>
                      <TableCell align='right'>
                        <IconButton
                          disabled={furnitureCreateform.isSubmitting}
                          onClick={() => removeProtocolFurniture(keyItem.id)}
                        ></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {protocolKey.map((keyItem) => (
                    <TableRow
                      key={keyItem.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component='th' scope='row'>
                        {getKetTypeName(keyItem.keyType)}
                      </TableCell>
                      <TableCell align='right'> {keyItem.quantity}</TableCell>
                      <TableCell align='right'> {keyItem.comment}</TableCell>
                      <TableCell align='right'>
                        <IconButton
                          disabled={furnitureCreateform.isSubmitting}
                          onClick={() => removeProtocolFurniture(keyItem.id)}
                        >
                          <DeleteForever />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              </TableBody>
            )}
          </Table>
        </TableContainer>
      )}

      {!isReadOnly && !!filteredSelectorTypes.length && (
        <>
          <Typography variant='caption' fontWeight={700}>
            Dodaj klucz
          </Typography>
          <form onSubmit={furnitureCreateform.handleSubmit}>
            <FormControl fullWidth sx={{ my: 2 }} error={!!furnitureCreateform.errors.keyType}>
              <InputLabel id='keyType'>Typ klucz</InputLabel>
              <Select
                disabled={furnitureCreateform.isSubmitting}
                labelId='keyType'
                value={furnitureCreateform.values.keyType}
                label='keyType'
                onChange={({ target }) =>
                  furnitureCreateform.setFieldValue('keyType', target.value)
                }
              >
                {filteredSelectorTypes.map((keyType) => (
                  <MenuItem key={keyType.id} value={keyType.name}>
                    {keyType.name}
                  </MenuItem>
                ))}
              </Select>
              {!!furnitureCreateform.errors.keyType && (
                <FormHelperText>{furnitureCreateform.errors.keyType}</FormHelperText>
              )}
            </FormControl>
            <TextField
              error={!!furnitureCreateform.errors.quantity}
              helperText={furnitureCreateform.errors.quantity}
              fullWidth
              sx={{ my: 2 }}
              id='quantity'
              label='Ilość'
              variant='outlined'
              onChange={furnitureCreateform.handleChange}
              value={furnitureCreateform.values.quantity}
              disabled={furnitureCreateform.isSubmitting}
            />
            <TextField
              error={!!furnitureCreateform.errors.comment}
              helperText={furnitureCreateform.errors.comment}
              fullWidth
              sx={{ my: 2 }}
              id='comment'
              label='Komentarz'
              variant='outlined'
              onChange={furnitureCreateform.handleChange}
              value={furnitureCreateform.values.comment}
              disabled={furnitureCreateform.isSubmitting}
            />
            <Button
              fullWidth
              type='submit'
              variant='contained'
              loading={furnitureCreateform.isSubmitting}
            >
              Dodaj
            </Button>
          </form>
        </>
      )}
    </Box>
  )
}

export default ProtocolKeys
